import { useState, useMemo } from "react";
import { IUser } from "../../../../api/homesites/homesites.types";
import { Community } from "../../../../api/communities/types";
import { HomeSitesDataIndex } from "../../../../reactQuery/homeSitesDataIndex/types";
import { useSnackbarContext } from "../../../../components/globalSnackNotify/globalSnackNotifyContext";
import { useUpdateHomeSite } from "../../../../reactQuery/homeSites/homsites/homeSiteQuery";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateCommunity } from "../../../../reactQuery/community/community";

interface UseBulkAssignConciergeProps {
     userInfo: any;
     homesites: HomeSitesDataIndex[];
     communities?: Community[];
     conciergesWithSelf: IUser[];
     currentCommunity?: string;
}

export const useBulkAssignConcierge = ({
     userInfo,
     homesites,
     communities = [],
     conciergesWithSelf,
     currentCommunity = "",
}: UseBulkAssignConciergeProps) => {
     // Get current user as concierge option
     const currentUserAsConcierge = useMemo(() => {
          if (!userInfo?.email || !userInfo?.name) return null;
          return conciergesWithSelf.find((c) => c.email === userInfo.email) || null;
     }, [userInfo?.email, userInfo?.name, conciergesWithSelf]);

     const { showSnackbar } = useSnackbarContext();
     const queryClient = useQueryClient();
     const updateHomeSite = useUpdateHomeSite(userInfo.userDivCode);
     const updateCommunity = useUpdateCommunity(userInfo.userDivCode);

     const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
     const [selectedBulkConcierge, setSelectedBulkConcierge] = useState<IUser | null>(null);
     const [selectedBulkCommunity, setSelectedBulkCommunity] = useState<string>("");
     const [isBulkAssigning, setIsBulkAssigning] = useState(false);
     const [bulkAssignProgress, setBulkAssignProgress] = useState({ current: 0, total: 0 });
     const [initialConcierge, setInitialConcierge] = useState<IUser | null>(null);
     const [initialCommunity, setInitialCommunity] = useState<string>("");
     const [unassignConfirmDialogOpen, setUnassignConfirmDialogOpen] = useState(false);
     const [communityToUnassign, setCommunityToUnassign] = useState<string>("");
     const [assignConfirmDialogOpen, setAssignConfirmDialogOpen] = useState(false);
     const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
     const [communityToReassign, setCommunityToReassign] = useState<string>("");
     const [targetConcierge, setTargetConcierge] = useState<IUser | null>(null);

     // Check if there are changes from initial values
     const hasChanges = useMemo(() => {
          const conciergeChanged = selectedBulkConcierge?.email !== initialConcierge?.email ||
               selectedBulkConcierge?.name !== initialConcierge?.name;
          const communityChanged = selectedBulkCommunity !== initialCommunity;
          return conciergeChanged || communityChanged;
     }, [selectedBulkConcierge, initialConcierge, selectedBulkCommunity, initialCommunity]);

     const handleBulkAssignClick = () => {
          setBulkAssignDialogOpen(true);
          // Pre-select current user as concierge
          const initialConciergeValue = currentUserAsConcierge;

          // Find first assigned community for the current user
          let initialCommunityValue = currentCommunity || "";
          if (initialConciergeValue && !initialCommunityValue) {
               const userCommunityMap = new Map<string, { assigned: number; available: number }>();
               const userEmailNorm = (initialConciergeValue.email || "").trim().toLowerCase();

               homesites?.forEach((hs) => {
                    if (!hs.communityName || hs.conciergeSource === "SF Opportunity") return;

                    const isAssigned =
                         !!hs.conciergeEmail &&
                         (hs.conciergeEmail || "").trim().toLowerCase() === userEmailNorm;
                    const isUnassigned = !hs.conciergeName && !hs.conciergeEmail;

                    const current = userCommunityMap.get(hs.communityName) || { assigned: 0, available: 0 };

                    if (isAssigned) {
                         current.assigned++;
                    } else if (isUnassigned) {
                         current.available++;
                    }

                    userCommunityMap.set(hs.communityName, current);
               });

               const assignedCommunities = Array.from(userCommunityMap.entries())
                    .filter(([_, counts]) => counts.assigned > 0)
                    .map(([community]) => community)
                    .sort();

               if (assignedCommunities.length > 0) {
                    initialCommunityValue = assignedCommunities[0];
               }
          }

          setSelectedBulkConcierge(initialConciergeValue);
          setSelectedBulkCommunity(initialCommunityValue);
          // Store initial values for cancel functionality
          setInitialConcierge(initialConciergeValue);
          setInitialCommunity(initialCommunityValue);
     };

     const handleUnassignClick = (communityName: string) => {
          setCommunityToUnassign(communityName);
          setUnassignConfirmDialogOpen(true);
     };

     const handleUnassignConfirm = async () => {
          if (!selectedBulkConcierge || !communityToUnassign) return;

          setUnassignConfirmDialogOpen(false);
          await handleUnassignFromCommunity(communityToUnassign);
          setCommunityToUnassign("");
          setInitialConcierge(selectedBulkConcierge);
          setInitialCommunity(selectedBulkCommunity);
     };

     const handleUnassignCancel = () => {
          setUnassignConfirmDialogOpen(false);
          setCommunityToUnassign("");
     };

     const handleReassignClick = (communityName: string) => {
          setCommunityToReassign(communityName);
          setReassignDialogOpen(true);
     };

     const handleReassignCancel = () => {
          setReassignDialogOpen(false);
          setCommunityToReassign("");
          setTargetConcierge(null);
     };

     const handleReassignConfirm = async () => {
          if (!selectedBulkConcierge || !communityToReassign || !targetConcierge) return;

          setReassignDialogOpen(false);

          const homesitesToUpdate = homesites?.filter((hs) => hs.communityName === communityToReassign) || [];

          if (homesitesToUpdate.length === 0) {
               showSnackbar("No homesites found in this community", "info");
               return;
          }

          // Get communityId from the first homesite
          const firstHomesite = homesitesToUpdate[0];
          if (!firstHomesite.communityId) {
               showSnackbar("Community ID not found", "error");
               return;
          }

          // Filter homesites that have the current concierge assigned by email (excluding Salesforce)
          const selectedEmailNorm = (selectedBulkConcierge.email || "").trim().toLowerCase();
          const homesitesNeedingUpdate = homesitesToUpdate.filter(
               (hs) =>
                    hs.conciergeSource !== "SF Opportunity" &&
                    !!hs.conciergeEmail &&
                    (hs.conciergeEmail || "").trim().toLowerCase() === selectedEmailNorm
          );

          if (homesitesNeedingUpdate.length === 0) {
               showSnackbar("No homesites have this concierge assigned in this community", "info");
               return;
          }

          // All phases for this community code (so community API is patched for each phase)
          const communityCode = firstHomesite.communityCode;
          const divisionCode = firstHomesite.divisionCode;
          const communitiesToPatch: Array<{ id: string; divisionCode: string }> = communities.length > 0
               ? communities.filter(
                    (c) => (c.communityCode || "") === communityCode && (c.divisionCode || "") === divisionCode
               )
               : [{ id: firstHomesite.communityId, divisionCode }];

          setIsBulkAssigning(true);
          const totalUpdates = communitiesToPatch.length + homesitesNeedingUpdate.length;
          setBulkAssignProgress({ current: 0, total: totalUpdates });

          try {
               // Update each community (phase) with new concierge assignment
               for (let c = 0; c < communitiesToPatch.length; c++) {
                    await updateCommunity.mutateAsync({
                         id: communitiesToPatch[c].id,
                         divisionCode,
                         conciergeName: targetConcierge.name,
                         conciergeEmail: targetConcierge.email,
                         updatedBy: userInfo?.email || userInfo?.name || "",
                    });
                    setBulkAssignProgress({ current: c + 1, total: totalUpdates });
               }

               // Update each homesite individually to reassign concierge
               for (let i = 0; i < homesitesNeedingUpdate.length; i++) {
                    const homesite = homesitesNeedingUpdate[i];
                    try {
                         await updateHomeSite.mutateAsync({
                              divisionCode: homesite.divisionCode,
                              homesiteId: homesite.homeSiteId,
                              body: {
                                   patches: [
                                        {
                                             op: "add",
                                             property: "/conciergeName",
                                             from: homesite.conciergeName,
                                             to: targetConcierge.name,
                                        },
                                        {
                                             op: "add",
                                             property: "/conciergeEmail",
                                             from: homesite.conciergeEmail,
                                             to: targetConcierge.email,
                                        },
                                   ],
                              },
                         });
                         setBulkAssignProgress({ current: communitiesToPatch.length + i + 1, total: totalUpdates });
                    } catch (homesiteError) {
                         console.error(`Error updating homesite ${homesite.homeSiteId}:`, homesiteError);
                         // Continue with other homesites even if one fails
                    }
               }

               setIsBulkAssigning(false);

               showSnackbar(`Successfully reassigned ${homesitesNeedingUpdate.length} homesite${homesitesNeedingUpdate.length === 1 ? "" : "s"} from ${selectedBulkConcierge.name} to ${targetConcierge.name} in ${communityToReassign}`, "success");

               setCommunityToReassign("");
               setTargetConcierge(null);
               setInitialConcierge(selectedBulkConcierge);
               setInitialCommunity(selectedBulkCommunity);
          } catch (error) {
               console.error(`Error reassigning concierge from community ${firstHomesite.communityId}:`, error);
               setIsBulkAssigning(false);
               showSnackbar(`Failed to reassign concierge in ${communityToReassign}`, "error");
          }

          // Refresh the data by invalidating queries
          queryClient.invalidateQueries({ queryKey: ["homeSites", userInfo.userDivCode] });
          queryClient.invalidateQueries({ queryKey: ["communities", userInfo.userDivCode] });
     };

     const handleUnassignFromCommunity = async (communityName: string) => {
          if (!selectedBulkConcierge) return;

          const homesitesToUpdate = homesites?.filter((hs) => hs.communityName === communityName) || [];

          if (homesitesToUpdate.length === 0) {
               showSnackbar("No homesites found in this community", "info");
               return;
          }

          // Get communityId from the first homesite (all homesites in the same community should have the same communityId)
          const firstHomesite = homesitesToUpdate[0];
          if (!firstHomesite.communityId) {
               showSnackbar("Community ID not found", "error");
               return;
          }

          // Filter homesites that have this concierge assigned by email (excluding Salesforce)
          const selectedEmailNorm = (selectedBulkConcierge.email || "").trim().toLowerCase();
          const homesitesNeedingUpdate = homesitesToUpdate.filter(
               (hs) =>
                    hs.conciergeSource !== "SF Opportunity" &&
                    !!hs.conciergeEmail &&
                    (hs.conciergeEmail || "").trim().toLowerCase() === selectedEmailNorm
          );

          if (homesitesNeedingUpdate.length === 0) {
               showSnackbar("No homesites have this concierge assigned in this community", "info");
               return;
          }

          // All phases for this community code
          const communityCode = firstHomesite.communityCode;
          const divisionCode = firstHomesite.divisionCode;
          const communitiesToPatch: Array<{ id: string; divisionCode: string }> = communities.length > 0
               ? communities.filter(
                    (c) => (c.communityCode || "") === communityCode && (c.divisionCode || "") === divisionCode
               )
               : [{ id: firstHomesite.communityId, divisionCode }];

          setIsBulkAssigning(true);
          const totalUpdates = communitiesToPatch.length + homesitesNeedingUpdate.length;
          setBulkAssignProgress({ current: 0, total: totalUpdates });

          try {
               // Update each community (phase) to unassign concierge (set to null)
               for (let c = 0; c < communitiesToPatch.length; c++) {
                    await updateCommunity.mutateAsync({
                         id: communitiesToPatch[c].id,
                         divisionCode,
                         conciergeName: null,
                         conciergeEmail: null,
                         updatedBy: userInfo?.email || userInfo?.name || "",
                    });
                    setBulkAssignProgress({ current: c + 1, total: totalUpdates });
               }

               // Update each homesite individually to unassign concierge
               for (let i = 0; i < homesitesNeedingUpdate.length; i++) {
                    const homesite = homesitesNeedingUpdate[i];
                    try {
                         await updateHomeSite.mutateAsync({
                              divisionCode: homesite.divisionCode,
                              homesiteId: homesite.homeSiteId,
                              body: {
                                   patches: [
                                        {
                                             op: "add",
                                             property: "/conciergeName",
                                             from: homesite.conciergeName,
                                             to: null,
                                        },
                                        {
                                             op: "add",
                                             property: "/conciergeEmail",
                                             from: homesite.conciergeEmail,
                                             to: null,
                                        },
                                   ],
                              },
                         });
                         setBulkAssignProgress({ current: communitiesToPatch.length + i + 1, total: totalUpdates });
                    } catch (homesiteError) {
                         console.error(`Error updating homesite ${homesite.homeSiteId}:`, homesiteError);
                         // Continue with other homesites even if one fails
                    }
               }

               setIsBulkAssigning(false);

               showSnackbar(`Successfully unassigned concierge from ${communityName}`, "success");
          } catch (error) {
               console.error(`Error unassigning concierge from community ${firstHomesite.communityId}:`, error);
               setIsBulkAssigning(false);
               showSnackbar(`Failed to unassign concierge from ${communityName}`, "error");
          }

          // Refresh the data by invalidating queries
          queryClient.invalidateQueries({ queryKey: ["homeSites", userInfo.userDivCode] });
          queryClient.invalidateQueries({ queryKey: ["communities", userInfo.userDivCode] });
     };

     const handleBulkAssignConfirm = () => {
          if (!selectedBulkConcierge || !selectedBulkConcierge.name || !selectedBulkConcierge.email) {
               showSnackbar("Please select a concierge", "error");
               return;
          }

          if (!selectedBulkCommunity) {
               showSnackbar("Please select a community", "error");
               return;
          }

          // Open confirmation dialog
          setAssignConfirmDialogOpen(true);
     };

     const handleBulkAssignExecute = async () => {
          if (!selectedBulkConcierge || !selectedBulkConcierge.name || !selectedBulkConcierge.email) {
               showSnackbar("Please select a concierge", "error");
               return;
          }

          if (!selectedBulkCommunity) {
               showSnackbar("Please select a community", "error");
               return;
          }

          setAssignConfirmDialogOpen(false);

          const homesitesToUpdate = homesites?.filter((hs) => hs.communityName === selectedBulkCommunity) || [];

          if (homesitesToUpdate.length === 0) {
               showSnackbar("No homesites to assign", "info");
               return;
          }

          // Get communityId from the first homesite (all homesites in the same community should have the same communityId)
          const firstHomesite = homesitesToUpdate[0];
          if (!firstHomesite.communityId) {
               showSnackbar("Community ID not found", "error");
               return;
          }

          // Only include homesites that are:
          // 1. Not from Salesforce
          // 2. Not already assigned to the selected concierge
          // 3. Not already assigned to any other concierge (only unassigned homesites)
          const homesitesNeedingUpdate = homesitesToUpdate.filter(
               (hs) =>
                    hs.conciergeSource !== "SF Opportunity" &&
                    !(hs.conciergeName === selectedBulkConcierge.name && hs.conciergeEmail === selectedBulkConcierge.email) &&
                    !(hs.conciergeName && hs.conciergeEmail) // Exclude homesites assigned to other concierges
          );

          if (homesitesNeedingUpdate.length === 0) {
               showSnackbar("No unassigned homesites available. All homesites are either assigned to a concierge or managed by Salesforce", "info");
               setIsBulkAssigning(false);
               return;
          }

          // All phases for this community code
          const communityCode = firstHomesite.communityCode;
          const divisionCode = firstHomesite.divisionCode;
          const communitiesToPatch: Array<{ id: string; divisionCode: string }> = communities.length > 0
               ? communities.filter(
                    (c) => (c.communityCode || "") === communityCode && (c.divisionCode || "") === divisionCode
               )
               : [{ id: firstHomesite.communityId, divisionCode }];

          setIsBulkAssigning(true);
          const totalUpdates = communitiesToPatch.length + homesitesNeedingUpdate.length;
          setBulkAssignProgress({ current: 0, total: totalUpdates });

          try {
               // Update each community (phase) with concierge assignment
               for (let c = 0; c < communitiesToPatch.length; c++) {
                    await updateCommunity.mutateAsync({
                         id: communitiesToPatch[c].id,
                         divisionCode,
                         conciergeName: selectedBulkConcierge.name,
                         conciergeEmail: selectedBulkConcierge.email,
                         updatedBy: userInfo?.email || userInfo?.name || "",
                    });
                    setBulkAssignProgress({ current: c + 1, total: totalUpdates });
               }

               // Update each homesite individually
               for (let i = 0; i < homesitesNeedingUpdate.length; i++) {
                    const homesite = homesitesNeedingUpdate[i];
                    try {
                         await updateHomeSite.mutateAsync({
                              divisionCode: homesite.divisionCode,
                              homesiteId: homesite.homeSiteId,
                              body: {
                                   patches: [
                                        {
                                             op: "add",
                                             property: "/conciergeName",
                                             from: homesite.conciergeName || null,
                                             to: selectedBulkConcierge.name,
                                        },
                                        {
                                             op: "add",
                                             property: "/conciergeEmail",
                                             from: homesite.conciergeEmail || null,
                                             to: selectedBulkConcierge.email,
                                        },
                                   ],
                              },
                         });
                         setBulkAssignProgress({ current: communitiesToPatch.length + i + 1, total: totalUpdates });
                    } catch (homesiteError) {
                         console.error(`Error updating homesite ${homesite.homeSiteId}:`, homesiteError);
                         // Continue with other homesites even if one fails
                    }
               }

               setIsBulkAssigning(false);
               setInitialConcierge(selectedBulkConcierge);
               setInitialCommunity(selectedBulkCommunity);

               const communityText = selectedBulkCommunity ? ` in ${selectedBulkCommunity}` : "";
               showSnackbar(`Successfully assigned homesites${communityText} to ${selectedBulkConcierge.name}`, "success");
          } catch (error) {
               console.error(`Error assigning concierge to community ${firstHomesite.communityId}:`, error);
               setIsBulkAssigning(false);
               showSnackbar(`Failed to assign homesites in ${selectedBulkCommunity}`, "error");
          }

          queryClient.invalidateQueries({ queryKey: ["homeSites", userInfo.userDivCode] });
          queryClient.invalidateQueries({ queryKey: ["communities", userInfo.userDivCode] });
     };

     const handleAssignConfirmCancel = () => {
          setAssignConfirmDialogOpen(false);
     };

     const handleBulkAssignCancel = () => {
          if (!isBulkAssigning) {
               // Reset to initial values instead of closing
               setSelectedBulkConcierge(initialConcierge);
               setSelectedBulkCommunity(initialCommunity);
          }
     };

     const handleDialogClose = () => {
          if (!isBulkAssigning) {
               setBulkAssignDialogOpen(false);
               setSelectedBulkConcierge(null);
               setSelectedBulkCommunity("");
               setInitialConcierge(null);
               setInitialCommunity("");
          }
     };

     const handleConciergeChange = (conciergeName: string) => {
          const concierge = conciergesWithSelf.find((c) => c.name === conciergeName);
          setSelectedBulkConcierge(concierge || null);

          // Auto-populate first assigned community for the selected concierge (identify by email)
          if (concierge) {
               const conciergeCommunityMap = new Map<string, { assigned: number; available: number }>();
               const selectedEmailNorm = (concierge.email || "").trim().toLowerCase();

               homesites?.forEach((hs) => {
                    if (!hs.communityName || hs.conciergeSource === "SF Opportunity") return;

                    const isAssigned =
                         !!hs.conciergeEmail &&
                         (hs.conciergeEmail || "").trim().toLowerCase() === selectedEmailNorm;
                    const isUnassigned = !hs.conciergeName && !hs.conciergeEmail;

                    const current = conciergeCommunityMap.get(hs.communityName) || { assigned: 0, available: 0 };

                    if (isAssigned) {
                         current.assigned++;
                    } else if (isUnassigned) {
                         current.available++;
                    }

                    conciergeCommunityMap.set(hs.communityName, current);
               });

               const assignedCommunities = Array.from(conciergeCommunityMap.entries())
                    .filter(([_, counts]) => counts.assigned > 0)
                    .map(([community]) => community)
                    .sort();

               // Only auto-select the concierge's first community when user hasn't already selected a community
               if (assignedCommunities.length > 0) {
                    setSelectedBulkCommunity((prev) => (prev === "" ? assignedCommunities[0] : prev));
               }
          } else {
               setSelectedBulkCommunity("");
          }
     };

     const handleCommunityChange = (community: string) => {
          setSelectedBulkCommunity(community);
     };

     return {
          // State
          bulkAssignDialogOpen,
          selectedBulkConcierge,
          selectedBulkCommunity,
          isBulkAssigning,
          bulkAssignProgress,
          unassignConfirmDialogOpen,
          communityToUnassign,
          assignConfirmDialogOpen,
          reassignDialogOpen,
          communityToReassign,
          targetConcierge,
          hasChanges,
          // Setters
          setSelectedBulkCommunity,
          setTargetConcierge,
          handleCommunityChange,
          // Handlers
          handleBulkAssignClick,
          handleUnassignClick,
          handleUnassignConfirm,
          handleUnassignCancel,
          handleReassignClick,
          handleReassignCancel,
          handleReassignConfirm,
          handleBulkAssignConfirm,
          handleBulkAssignExecute,
          handleAssignConfirmCancel,
          handleBulkAssignCancel,
          handleDialogClose,
          handleConciergeChange,
     };
};
